<template>
  <v-card
    v-click-outside="handleFocusOut"
    flat
    tabindex="-1"
  >
    <div class="select-clear-buttons">
      <span
        data-testid="select-all-options"
        @click="handleSelectAll"
      >
        Select All
      </span>
      <span
        data-testid="clear-options"
        @click="handleClearSelections"
      >
        Clear
      </span>
    </div>
    <div class="filter-spec-checkbox-options-container">
      <div
        v-for="checkbox in localFilter.options"
        :id="'group-' + stripLabel(checkbox)"
        :key="checkbox.label"
      >
        <label
          class="toggle-item"
          @click.stop
        >
          <input
            :id="'group-checkbox-' + stripLabel(checkbox)"
            type="checkbox"
            class="checkbox outlined-input"
            :checked="checkbox.selected"
            @change="checkbox.selected = $event.target.checked"
          >
          <span class="checkmark" />
        </label>
        <div
          class="group-label"
          @click="checkbox.selected = !checkbox.selected"
        >
          {{ optionLabel(checkbox.label) }}
        </div>
      </div>
    </div>
    <FilterSpecButtons
      @close-filter-specification="$emit('close-filter-specification')"
      @apply-filters="applyFilters"
    />
  </v-card>
</template>

<script lang="ts">
import {toRaw} from 'vue';
import FilterSpecButtons from './FilterSpecButtons.vue';

export default {
  name: 'FilterSpecCheckboxes',
  components: {
    FilterSpecButtons,
  },
  props: {
    /** Carries `options`, an array of `{label, selected}`. */
    filter: {
      type: Object,
      required: true,
    },
    /** How many items each option matches, keyed by label. Labels with no entry show no count. */
    counts: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close-filter-specification', 'update-filters'],
  data() {
    return {
      localFilter: structuredClone(toRaw(this.filter)),
    };
  },
  mounted() {
    this.focusOnElement();
  },
  methods: {
    // Extracted from mounted so tests can mount without a real element.
    focusOnElement() {
      this.$el.focus();
    },
    stripLabel(checkbox) {
      return checkbox.label.replace(' ', '-');
    },
    optionLabel(label) {
      const count = this.counts[label];
      return count == null ? label : `${label} (${count})`;
    },
    applyFilters() {
      this.$emit('update-filters', this.localFilter);
      this.$emit('close-filter-specification');
    },
    handleFocusOut() {
      if (this.$el.matches(':focus-within')) return;
      this.$emit('close-filter-specification');
    },
    handleSelectAll() {
      this.localFilter.options.forEach((option) => {
        option.selected = true;
      });
    },
    handleClearSelections() {
      this.localFilter.options.forEach((option) => {
        option.selected = false;
      });
    },
  },
};
</script>

<style scoped lang="scss">
.select-clear-buttons {
  text-decoration: underline;
  color: main.$npOrange;
  cursor: pointer;
  gap: 1rem;
  display: flex;
}
.group-label {
  cursor: pointer;
  margin-left: 25px;
  margin-top: -2px;
  font-family: main.$fontPrimary;
  color: main.$npGray;
}

.filter-spec-checkbox-options-container {
  max-height: 500px;
  min-width: 265px;
  display: flex;
  flex-direction: column;
  overflow: scroll;
  align-items: flex-start;
  gap: 1rem;
}

.toggle-item {
  display: block;
  position: relative;
  cursor: pointer;
  margin-top: 2px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
    &:checked~.checkmark {
      background-color: main.$npOrange;
      border: none;
      &:after {
        display: block;
      }
    }
  }
  .checkmark {
    position: absolute;
    left: 0px;
    height: 16px;
    width: 16px;
    border-radius: 2px;
    border: 2px solid main.$npGray;
    &:after {
      content: "";
      position: absolute;
      display: none;
      left: 5px;
      top: 1px;
      width: 6px;
      height: 10px;
      border: solid white;
      border-width: 0 2px 2px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
}
</style>
